<script lang="ts">
  import { inertia } from '@inertiajs/svelte';
  import { Button } from '$lib/components/ui/button';
  import { Zap, Gauge, Target, ShieldCheck, ArrowRight, X, Phone, MessageSquare } from 'lucide-svelte';
  import { fly, fade } from 'svelte/transition';

  // 弹窗控制状态 (Svelte 5 符文)
  let showContactModal = $state(false);

  // 核心商业价值主张
  const features = [
    { 
      icon: Zap, 
      title: "比传统网站快 5-10 倍", 
      description: "拒绝臃肿卡顿的旧系统。我们的网页实现瞬时开启，这种极速体验不仅能留住每一位访客，更让您的生意在起跑线上就甩开对手。" 
    },
    { 
      icon: Target, 
      title: "首页 SEO 默认满分", 
      description: "您不需要懂任何搜索排名技术。我们从底层确保您的网站符合搜索引擎最高评分标准，让您的品牌天生具备极强的自然获客能力。" 
    },
    { 
      icon: ShieldCheck, 
      title: "一站式全包，终身维护", 
      description: "无需懂技术，更无需操心维护。我们提供包含域名、极速服务器及日常安全更新的全托管服务。您只需专注生意，剩下的交给我们。" 
    }
  ];

  function toggleModal() {
    showContactModal = !showContactModal;
  }
</script>

<svelte:head>
  <title>分太极光 Fentime Aurora | 比 WordPress 快 5~10 倍的极速建站系统</title>
  
  <meta name="description" content="分太极光（Fentime Aurora）由光焰科技打造，专为高端商业品牌与外贸企业设计的极速引擎。提供行业领先的秒开体验，确保搜索引擎评分默认满分，助您降低获客成本。一站式专家级托管，让您专注业务增长，无需操心任何维护细节。" />
  
  <meta name="keywords" content="分太极光,Fentime Aurora,光焰科技,极速建站,商业网站制作,高性能企业官网,外贸独立站,SEO建站,品牌数字化方案" />
  
  <meta property="og:title" content="分太极光 Fentime Aurora | 极速商业建站系统" />
  <meta property="og:description" content="比 WordPress 快 5~10 倍，默认搜索引擎评分满分。专业一站式品牌托管服务。" />
  <meta property="og:type" content="website" />
</svelte:head>

<div class="min-h-screen bg-zinc-950 text-zinc-100 selection:bg-purple-900/40 relative overflow-hidden">
  
  <div class="absolute inset-0 pointer-events-none z-0">
    <div class="absolute -top-40 -left-40 w-96 h-96 bg-purple-600/10 rounded-full blur-[128px]"></div>
    <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1/2 h-1/2 bg-purple-900/10 rounded-full blur-[160px]"></div>
    <div class="absolute -bottom-40 -right-40 w-80 h-80 bg-purple-500/10 rounded-full blur-[112px]"></div>
  </div>

  <header class="border-b border-zinc-800 bg-zinc-950/80 backdrop-blur-sm sticky top-0 z-50">
    <nav class="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
      <div class="flex items-center gap-2">
        <div class="w-7 h-7 bg-purple-600 rounded-lg flex items-center justify-center font-bold text-white text-lg">A</div>
        <span class="text-xl font-bold tracking-tight">Fentime <span class="text-purple-400">Aurora</span></span>
      </div>
      
      <div class="flex items-center gap-4">
        <Button variant="ghost" class="text-zinc-300 hover:text-white hover:bg-zinc-800 p-0 overflow-hidden">
          <a href="/login" use:inertia class="px-4 py-2 flex items-center h-full w-full">
            登录
          </a>
        </Button>
        <Button onclick={toggleModal} class="bg-purple-600 hover:bg-purple-500 text-white px-5">
          立即咨询
        </Button>
      </div>
    </nav>
  </header>

  <main class="max-w-7xl mx-auto px-6 py-20 md:py-32 relative z-10">
    <div class="text-center space-y-8 flex flex-col items-center">
      <div class="inline-flex items-center gap-2 border border-purple-500/30 bg-purple-950/30 px-4 py-1.5 rounded-full text-purple-200 text-sm backdrop-blur-sm">
        <Zap class="h-4 w-4" />
        <span>更懂生意的网站，让转化更简单</span>
      </div>
      
      <h1 class="text-5xl md:text-7xl font-extrabold tracking-tighter leading-tight bg-gradient-to-b from-white to-zinc-300 bg-clip-text text-transparent">
        分太极光<br/>
        比 WordPress 快 10 倍的极速建站
      </h1>
      
      <p class="text-xl text-zinc-400 max-w-4xl leading-relaxed">
        别再把时间浪费在复杂缓慢的传统建站上了。我们为您提供默认 SEO 满分、极速秒开、且终身免维护的专业网站。一年仅需 980 元，把您的精力从技术坑里彻底解放出来。
      </p>
      
      <div class="pt-6 flex flex-col sm:flex-row gap-4">
        <Button size="lg" onclick={toggleModal} class="bg-purple-600 hover:bg-purple-500 text-white group px-8 py-6 h-auto text-lg">
          构建我的空间
          <ArrowRight class="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
        </Button>
        
        <Button 
          size="lg" 
          variant="outline" 
          class="border-zinc-700 bg-zinc-900/50 hover:bg-zinc-800 text-zinc-100 hover:text-white transition-colors h-auto p-0 overflow-hidden"
        >
          <a href="#features" class="px-8 py-6 flex items-center h-full w-full text-lg">
            了解为何我们更快
          </a>
        </Button>
      </div>
    </div>

    <div id="features" class="pt-28 md:pt-40 grid md:grid-cols-3 gap-8">
      {#each features as feature, i}
        <div 
          class="border border-zinc-800 bg-zinc-900/50 p-8 rounded-2xl space-y-5 hover:border-purple-500/50 hover:bg-zinc-900 transition-all duration-300 backdrop-blur-sm group"
          in:fly={{ y: 20, duration: 400, delay: i * 100 }}
        >
          <div class="w-12 h-12 rounded-xl bg-purple-950 border border-purple-700 flex items-center justify-center text-purple-300 group-hover:bg-purple-600 group-hover:text-white transition-colors">
            <svelte:component this={feature.icon} class="h-6 w-6" />
          </div>
          <h3 class="text-2xl font-semibold tracking-tight text-white">{feature.title}</h3>
          <p class="text-zinc-400 text-base leading-relaxed">{feature.description}</p>
        </div>
      {/each}
    </div>

    <div class="mt-28 md:mt-40 border border-zinc-800 bg-zinc-900/30 p-12 rounded-3xl text-center space-y-8 backdrop-blur-sm">
      <div class="inline-flex gap-1.5 items-center justify-center text-purple-400">
        {#each Array(5) as _}
          <Gauge class="h-6 w-6" />
        {/each}
      </div>
      <h2 class="text-4xl md:text-5xl font-extrabold tracking-tighter text-white">您的生意，值得更高级的呈现</h2>
      <p class="text-zinc-400 text-lg max-w-2xl mx-auto">
        还在忍受那些加载缓慢、折腾半天的传统网站？现在就申请您的极速网站，从 SEO 优化到终身维护，我们为您提供专业的一站式托管服务。
      </p>
      <Button onclick={toggleModal} size="lg" class="bg-purple-600 hover:bg-purple-500 text-white group px-10 py-5 h-auto text-xl">
        立即获取定制报价
        <ArrowRight class="ml-2 h-6 w-6 group-hover:translate-x-1 transition-transform" />
      </Button>
    </div>
  </main>

  <footer class="border-t border-zinc-800 mt-28 py-12 bg-zinc-950/50 relative z-10">
    <div class="max-w-7xl mx-auto px-6 text-center space-y-6">
      <div class="flex flex-col md:flex-row items-center justify-center gap-2 md:gap-4 text-zinc-500 text-sm">
        <div class="flex items-center gap-2">
          <span class="text-zinc-400 font-semibold">分太极光 Fentime Aurora</span>
          <span class="text-zinc-700">|</span>
          <a 
            href="https://www.guangyantech.cn/" 
            target="_blank" 
            rel="noopener noreferrer" 
            class="hover:text-purple-400 transition-colors"
          >
            光焰科技 (北京) 有限公司
          </a>
        </div>
        <span class="hidden md:inline text-zinc-800">•</span>
        <p>© 2026 版权所有</p>
      </div>

      <div class="flex items-center justify-center">
        <a 
          href="https://beian.miit.gov.cn/" 
          target="_blank" 
          rel="noopener noreferrer" 
          class="text-zinc-600 hover:text-zinc-400 text-xs transition-colors tracking-wide"
        >
          京ICP备2021036370号
        </a>
      </div>
      
      <div class="text-[10px] uppercase tracking-[0.2em] text-zinc-800 flex items-center justify-center gap-4 pt-2">
        <span class="flex items-center gap-1.5"><ShieldCheck class="w-3 h-3" /> 全球极速引擎认证</span>
        <span class="flex items-center gap-1.5"><Target class="w-3 h-3" /> 商业引流架构</span>
        <span class="flex items-center gap-1.5"><Zap class="w-3 h-3" /> 专家级全托管服务</span>
      </div>
    </div>
  </footer>

  {#if showContactModal}
    <div 
      class="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
      transition:fade={{ duration: 200 }}
    >
      <button class="absolute inset-0 w-full h-full cursor-default" onclick={toggleModal} aria-label="close"></button>

      <div 
        class="bg-zinc-900 border border-zinc-800 p-8 rounded-3xl max-w-md w-full text-center relative shadow-[0_0_50px_-12px_rgba(147,51,234,0.3)] z-10"
        transition:fly={{ y: 20, duration: 300 }}
      >
        <button 
          onclick={toggleModal}
          class="absolute top-5 right-5 text-zinc-500 hover:text-white transition-colors"
        >
          <X class="h-6 w-6" />
        </button>

        <div class="space-y-6">
          <div class="inline-flex w-16 h-16 bg-purple-600/20 rounded-2xl items-center justify-center text-purple-500 mb-2">
            <MessageSquare class="h-8 w-8" />
          </div>
          
          <div>
            <h3 class="text-2xl font-bold text-white mb-2">获取专业定制方案</h3>
            <p class="text-zinc-400">请扫码添加产品专家微信，或直接拨打服务热线</p>
          </div>

          <div class="bg-white p-3 rounded-2xl inline-block shadow-inner">
            <img src="/images/wechat-qr.png" alt="微信二维码" class="w-48 h-48 rounded-lg" />
          </div>

          <div class="space-y-4">
            <div class="flex items-center justify-center gap-3 text-2xl font-mono font-bold text-purple-400 bg-zinc-950 py-3 rounded-xl border border-zinc-800">
              <Phone class="h-5 w-5" />
              <span>186-1146-8006</span>
            </div>
            <p class="text-zinc-500 text-sm">专业架构师一对一提供行业数字化建议</p>
          </div>

          <Button 
            variant="outline" 
            class="w-full border-zinc-800 hover:bg-zinc-800 text-zinc-400 mt-2"
            onclick={toggleModal}
          >
            返回浏览
          </Button>
        </div>
      </div>
    </div>
  {/if}

</div>

<style>
  :global(html) {
    scroll-behavior: smooth;
  }
</style>